import { type FormEvent, useEffect, useState } from 'react';
import {
  addDirectShare,
  createPublicShare,
  getOwnerShareState,
  revokeDirectShare,
  revokePublicShare,
  type DirectShareRecipient,
} from '@/features/sharing/services/sharingApi';
import { Button, CopyIcon, Input, Modal, Spinner, TrashIcon } from '@/shared/ui';
import { safeErrorMessage } from '@/shared/utils/errors';

const EMAIL_RE = /^\S+@\S+\.\S+$/;

type ShareVideoModalProps = {
  open: boolean;
  videoId: string;
  title: string;
  onClose: () => void;
};

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const element = document.createElement('textarea');
  element.value = value;
  element.setAttribute('readonly', '');
  element.style.position = 'fixed';
  element.style.top = '-9999px';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
}

export function ShareVideoModal({ open, videoId, title, onClose }: ShareVideoModalProps) {
  const [loading, setLoading] = useState(false);
  const [publicActive, setPublicActive] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');
  const [recipients, setRecipients] = useState<DirectShareRecipient[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setPublicUrl('');
    setError(null);
    setSuccess(null);
    void getOwnerShareState(videoId)
      .then((state) => {
        if (!active) return;
        setPublicActive(state.publicShare.active);
        setRecipients(state.directShares);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(safeErrorMessage(err, 'Could not load sharing settings.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, videoId]);

  const createLink = async () => {
    if (busy) return;
    setBusy('public');
    setError(null);
    setSuccess(null);
    try {
      const result = await createPublicShare(videoId);
      setPublicActive(result.active);
      setPublicUrl(result.url);
      setSuccess('Public link created.');
    } catch (err) {
      setError(safeErrorMessage(err, 'Could not create public link.'));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    if (!publicUrl || busy) return;
    setBusy('copy');
    setError(null);
    try {
      await copyText(publicUrl);
      setSuccess('Copied link.');
    } catch {
      setError('Could not copy the link.');
    } finally {
      setBusy(null);
    }
  };

  const revokeLink = async () => {
    if (busy) return;
    setBusy('revoke-public');
    setError(null);
    setSuccess(null);
    try {
      const state = await revokePublicShare(videoId);
      setPublicActive(state.active);
      setPublicUrl('');
      setSuccess('Public link revoked.');
    } catch (err) {
      setError(safeErrorMessage(err, 'Could not revoke public link.'));
    } finally {
      setBusy(null);
    }
  };

  const submitRecipient = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (busy) return;
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setSuccess(null);
      setError('Enter a valid email address.');
      return;
    }
    setBusy('direct');
    setError(null);
    setSuccess(null);
    try {
      const recipient = await addDirectShare(videoId, trimmed);
      setRecipients((current) => {
        const withoutExisting = current.filter((entry) => entry.id !== recipient.id);
        return [recipient, ...withoutExisting];
      });
      setEmail('');
      setSuccess('Recipient added.');
    } catch (err) {
      setError(safeErrorMessage(err, 'Could not share with that email.'));
    } finally {
      setBusy(null);
    }
  };

  const removeRecipient = async (shareId: string) => {
    if (busy) return;
    setBusy(`remove-${shareId}`);
    setError(null);
    setSuccess(null);
    try {
      await revokeDirectShare(videoId, shareId);
      setRecipients((current) => current.filter((entry) => entry.id !== shareId));
      setSuccess('Recipient removed.');
    } catch (err) {
      setError(safeErrorMessage(err, 'Could not remove recipient.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      title={`Share ${title}`}
      description="Anyone with this public link can watch and download this video."
      size="md"
      className="share-modal"
      footer={
        <Button onClick={onClose} disabled={Boolean(busy)}>
          Done
        </Button>
      }
    >
      {loading ? (
        <div className="share-modal__loading">
          <Spinner size={22} label="Loading sharing settings" />
        </div>
      ) : (
        <div className="share-modal__content">
          {error ? (
            <div className="share-modal__message share-modal__message--error" role="alert">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="share-modal__message" role="status">
              {success}
            </div>
          ) : null}

          <section className="share-modal__section">
            <div className="share-modal__section-head">
              <h4>Public link</h4>
              {publicActive ? <span>Active</span> : <span>Off</span>}
            </div>
            {publicUrl ? (
              <div className="share-modal__link-row">
                <input value={publicUrl} readOnly aria-label="Public share link" />
                <Button size="sm" iconLeft={<CopyIcon size={15} />} onClick={() => void copyLink()} disabled={Boolean(busy)}>
                  {busy === 'copy' ? 'Copying…' : 'Copy'}
                </Button>
              </div>
            ) : publicActive ? (
              <p className="share-modal__hint">The link is active. Create a new link to rotate it.</p>
            ) : null}
            <div className="share-modal__actions">
              <Button size="sm" onClick={() => void createLink()} disabled={Boolean(busy)}>
                {publicActive ? 'Rotate link' : 'Create link'}
              </Button>
              {publicActive ? (
                <Button size="sm" variant="danger" onClick={() => void revokeLink()} disabled={Boolean(busy)}>
                  {busy === 'revoke-public' ? 'Revoking…' : 'Revoke'}
                </Button>
              ) : null}
            </div>
          </section>

          <section className="share-modal__section">
            <div className="share-modal__section-head">
              <h4>Direct share</h4>
              <span>{recipients.length}</span>
            </div>
            <form className="share-modal__recipient-form" noValidate onSubmit={(event) => void submitRecipient(event)}>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="recipient@example.com"
                aria-label="Recipient email"
              />
              <Button size="sm" type="submit" disabled={Boolean(busy) || !email.trim()}>
                {busy === 'direct' ? 'Adding…' : 'Add'}
              </Button>
            </form>

            {recipients.length > 0 ? (
              <ul className="share-modal__recipients">
                {recipients.map((recipient) => (
                  <li key={recipient.id}>
                    <span>{recipient.recipientEmail}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      iconLeft={<TrashIcon size={15} />}
                      onClick={() => void removeRecipient(recipient.id)}
                      disabled={Boolean(busy)}
                    >
                      {busy === `remove-${recipient.id}` ? 'Removing…' : 'Remove'}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="share-modal__hint">No direct recipients yet.</p>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
